import React from "react";
import { cn } from "../cn";

/** Depth signals *floating*, not *importance*. See ai-specs/specs/design-standards.md. */
export type SurfaceElevation = "flat" | "raised" | "overlay" | "modal";

export type SurfaceRadius = "lg" | "xl" | "2xl";

export type SurfacePadding = "none" | "sm" | "md" | "lg";

/**
 * Status tone. `accent` is Alchely's own voice; the rest carry clinical meaning
 * and must always be accompanied by an icon and a label at the call site, never
 * colour alone (WCAG 1.4.1).
 */
export type SurfaceTone =
  | "default"
  | "accent"
  | "critical"
  | "warning"
  | "success"
  | "info";

/** The subset of elements a surface is ever legitimately rendered as. */
export type SurfaceElement = "div" | "section" | "article" | "aside" | "li";

export type SurfaceProps = React.HTMLAttributes<HTMLElement> & {
  as?: SurfaceElement;
  elevation?: SurfaceElevation;
  radius?: SurfaceRadius;
  padding?: SurfacePadding;
  tone?: SurfaceTone;
  /** Adds hover and focus affordances. Use when the whole surface is clickable. */
  interactive?: boolean;
  /**
   * Marks the surface as holding AI-generated content, drawing the Alchely
   * spectrum as a rule across its top edge. Pair it with a visible label —
   * an `AiBadge` — so the provenance is never carried by colour alone.
   */
  aiGenerated?: boolean;
  children?: React.ReactNode;
};

const ELEVATION: Record<SurfaceElevation, string> = {
  // The default. A card is delimited by its border, not by a drop shadow.
  flat: "shadow-none",
  raised: "shadow-raised",
  overlay: "shadow-overlay",
  modal: "shadow-modal",
};

const RADIUS: Record<SurfaceRadius, string> = {
  lg: "rounded-lg",
  xl: "rounded-xl",
  "2xl": "rounded-2xl",
};

const PADDING: Record<SurfacePadding, string> = {
  none: "",
  sm: "p-4",
  md: "p-5 sm:p-6",
  lg: "p-6 sm:p-8",
};

const TONE: Record<SurfaceTone, string> = {
  default: "bg-surface-raised border-border-subtle",
  accent: "bg-accent-tint border-accent/20",
  critical: "bg-status-critical-tint border-status-critical/25",
  warning: "bg-status-warning-tint border-status-warning/25",
  success: "bg-status-success-tint border-status-success/25",
  info: "bg-status-info-tint border-status-info/25",
};

/**
 * The panel primitive every card, list row and section in the product is built
 * from. It exists so that "a card" is one decision made once rather than the
 * `bg-white dark:bg-gray-900 rounded-2xl border border-gray-200
 * dark:border-white/10 shadow-raised` string that was being retyped, and
 * drifting, across the app.
 */
const Surface: React.FC<SurfaceProps> = ({
  as: Tag = "div",
  elevation = "flat",
  radius = "xl",
  padding = "md",
  tone = "default",
  interactive = false,
  aiGenerated = false,
  className,
  children,
  ...rest
}) => (
  <Tag
    className={cn(
      "relative border",
      RADIUS[radius],
      PADDING[padding],
      TONE[tone],
      ELEVATION[elevation],
      interactive && [
        "transition-colors duration-[--duration-fast] ease-[--ease-standard]",
        "hover:border-border-strong hover:bg-surface-sunken/40",
        // The whole surface is the target, so it carries the focus ring itself.
        "focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-accent-text",
      ],
      // `overflow-hidden` keeps the spectrum rule inside the rounded corners.
      aiGenerated && "ai-spectrum-rule overflow-hidden",
      className
    )}
    {...rest}
  >
    {children}
  </Tag>
);

export default Surface;
